import React, { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getFoodItems, deleteFoodItem } from '../../services/api';
import { useApiQuery, useApiMutation } from '../../hooks/useApiQuery';
import { useSocket } from '../../context/SocketContext';
import useToast from '../../hooks/useToast';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';

const VendorFoodList = () => {
  const { showSuccess, showError } = useToast();
  const socket = useSocket();

  // ✅ React Query: Fetch food items
  const {
    data: items = [],
    isLoading,
    refetch: refetchItems,
  } = useApiQuery(
    ['food-items'],
    () => getFoodItems().then(res => res.data),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      onError: () => {
        console.error('Failed to fetch food items');
      },
    }
  );

  // ✅ React Query: Delete food item mutation
  const deleteMutation = useApiMutation(
    (id) => deleteFoodItem(id).then(res => res.data),
    {
      onSuccess: (_, id) => {
        showSuccess('Item deleted');
        // ✅ Optimistically update the list
        refetchItems();
      },
      onError: (error) => {
        showError(error.response?.data?.message || 'Failed to delete item');
      },
    }
  );

  // ✅ Socket listener for real-time updates
  React.useEffect(() => {
    if (!socket) return;

    const handleUpdate = () => {
      console.log('🔄 Food item changed, refreshing...');
      refetchItems();
    };

    socket.on('foodItemCreated', handleUpdate);
    socket.on('foodItemUpdated', handleUpdate);
    socket.on('foodItemDeleted', handleUpdate);

    return () => {
      socket.off('foodItemCreated', handleUpdate);
      socket.off('foodItemUpdated', handleUpdate);
      socket.off('foodItemDeleted', handleUpdate);
    };
  }, [socket, refetchItems]);

  // ✅ Handle delete
  const handleDelete = useCallback((id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    deleteMutation.mutate(id);
  }, [deleteMutation]);

  // ✅ Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="inline-block w-10 h-10 border-2 border-[#1672b9] border-t-transparent rounded-full animate-spin mb-3"></div>
          <p className="text-gray-500 text-sm">Loading food items...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-normal text-[#2b3445]">Food Items</h1>
        <Link
          to="/vendor/food/add"
          className="bg-[#1471b9] text-white px-4 py-2 rounded-[8px] text-sm font-bold hover:opacity-90 transition flex items-center gap-2"
        >
          <PlusIcon className="h-4 w-4" />
          Add New Item
        </Link>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-8 text-[#708090]">
          No food items yet. Click "Add New Item" to create one.
        </div>
      ) : (
        <div className="bg-white rounded-[15px] border border-[#dae1e7] shadow-[0px_1px_3px_0px_rgba(3,0,71,0.09)] overflow-hidden">
          <table className="min-w-full divide-y divide-[#dae1e7]">
            <thead className="bg-[#f8f9fa]">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#708090] uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#708090] uppercase">Price</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#708090] uppercase">Stock</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#708090] uppercase">Status</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-[#708090] uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#dae1e7]">
              {items.map((item) => (
                <tr key={item._id} className="hover:bg-[#f8f9fa] transition">
                  <td className="px-4 py-3 text-sm text-[#2b3445]">{item.name}</td>
                  <td className="px-4 py-3 text-sm text-[#2b3445]">
                    ₹{item.price.toFixed(2)}
                  </td>
                  <td className="px-4 py-3 text-sm text-[#2b3445]">{item.stock}</td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      item.isAvailable 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {item.isAvailable ? 'Available' : 'Unavailable'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right space-x-2">
                    <Link
                      to={`/vendor/food/edit/${item._id}`}
                      className="text-[#1471b9] hover:text-[#0f5a8a] transition inline-block"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </Link>
                    <button
                      onClick={() => handleDelete(item._id)}
                      disabled={deleteMutation.isPending}
                      className="text-red-500 hover:text-red-700 transition inline-block disabled:opacity-50"
                    >
                      {deleteMutation.isPending && deleteMutation.variables === item._id ? (
                        <span className="inline-block w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin"></span>
                      ) : (
                        <TrashIcon className="h-4 w-4" />
                      )}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default VendorFoodList;